// Leases of one unit that run at the same time. That should not happen (an
// earlier lease that was never ended, a move-in registered too early), but
// when it does every day is still charged once: the leases active that day
// share it.

const MS_PER_DAY = 86_400_000;

export interface IDayRange {
  from: string;
  to: string;
}

const dayTime = (iso: string): number =>
  new Date(`${iso.slice(0, 10)}T00:00:00Z`).getTime();

const isoDay = (time: number): string =>
  new Date(time).toISOString().slice(0, 10);

const clampToPeriod = (params: {
  range: IDayRange;
  period: IDayRange;
}): { from: number; to: number } | null => {
  const { range, period } = params;
  const from = Math.max(dayTime(range.from), dayTime(period.from));
  const to = Math.min(dayTime(range.to), dayTime(period.to));
  return from <= to ? { from, to } : null;
};

// Each range's weight in the occupied days of `period`: a day it has to
// itself counts 1, a day it shares with n − 1 other ranges counts 1 / n.
// Without overlaps this is each range's own day count; the weights always add
// up to the days covered by at least one range.
export const sharedDayWeights = (params: {
  ranges: IDayRange[];
  period: IDayRange;
}): number[] => {
  const { ranges, period } = params;
  const clamped = ranges.map(range => clampToPeriod({ range, period }));
  const bounds = [
    ...new Set(clamped.flatMap(c => (c ? [c.from, c.to + MS_PER_DAY] : []))),
  ].sort((a, b) => a - b);
  const weights = ranges.map(() => 0);
  for (let i = 0; i + 1 < bounds.length; i += 1) {
    const segmentStart = bounds[i];
    const active = clamped
      .map((c, index) => ({ c, index }))
      .filter(({ c }) => c && c.from <= segmentStart && c.to >= segmentStart);
    if (active.length === 0) continue;
    const days = Math.round((bounds[i + 1] - segmentStart) / MS_PER_DAY);
    for (const { index } of active) weights[index] += days / active.length;
  }
  return weights;
};

export interface ILeaseOverlap {
  firstIndex: number;
  secondIndex: number;
  from: string;
  to: string;
}

// Every pair of ranges that share days within `period`, with the shared
// stretch.
export const findOverlaps = (params: {
  ranges: IDayRange[];
  period: IDayRange;
}): ILeaseOverlap[] => {
  const { ranges, period } = params;
  const clamped = ranges.map(range => clampToPeriod({ range, period }));
  const out: ILeaseOverlap[] = [];
  clamped.forEach((first, firstIndex) => {
    clamped.slice(firstIndex + 1).forEach((second, offset) => {
      if (!first || !second) return;
      const from = Math.max(first.from, second.from);
      const to = Math.min(first.to, second.to);
      if (from > to) return;
      out.push({
        firstIndex,
        secondIndex: firstIndex + 1 + offset,
        from: isoDay(from),
        to: isoDay(to),
      });
    });
  });
  return out;
};

export interface ILeaseRef {
  id: string;
  unitId: string;
  startDate: string;
  endDate: string;
}

export interface IUnitLeaseOverlap {
  unitId: string;
  first: ILeaseRef;
  second: ILeaseRef;
  from: string;
  to: string;
}

// Leases of the same unit that share days within the settled cost periods:
// the costs of those days are split between them, which is rarely what the
// broker meant (usually the earlier lease was never ended).
export const unitLeaseOverlaps = (params: {
  leases: ILeaseRef[];
  periods: IDayRange[];
}): IUnitLeaseOverlap[] => {
  const { leases, periods } = params;
  if (periods.length === 0) return [];
  const span = {
    from: periods.map(p => p.from).sort()[0],
    to: periods
      .map(p => p.to)
      .sort()
      .reverse()[0],
  };
  const unitIds = [...new Set(leases.map(lease => lease.unitId))];
  return unitIds.flatMap(unitId => {
    const unitLeases = leases
      .filter(lease => lease.unitId === unitId)
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
    return findOverlaps({
      ranges: unitLeases.map(lease => ({
        from: lease.startDate,
        to: lease.endDate,
      })),
      period: span,
    })
      .filter(overlap =>
        periods.some(p => clampToPeriod({ range: overlap, period: p })),
      )
      .map(overlap => ({
        unitId,
        first: unitLeases[overlap.firstIndex],
        second: unitLeases[overlap.secondIndex],
        from: overlap.from,
        to: overlap.to,
      }));
  });
};
